import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { api } from '../../services/api.js';
import { geocode } from '../../services/mapbox.js';
import { useMapStore } from '../../stores/mapStore.js';
import { LoadingSpinner } from '../ui/index.js';
import type { Location, LocationSupport, Scene, GeoFeature } from '../../types/index.js';

// ─── Image resize utility ────────────────────────────────────────────────────
async function resizeImage(file: File, maxPx = 1200, quality = 0.72): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objUrl);
      const canvas = document.createElement('canvas');
      let { width, height } = img;
      if (width > maxPx || height > maxPx) {
        if (width >= height) { height = Math.round(height * maxPx / width); width = maxPx; }
        else { width = Math.round(width * maxPx / height); height = maxPx; }
      }
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d')!.drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = reject;
    img.src = objUrl;
  });
}

// ─── Geocode search hook ──────────────────────────────────────────────────────
// proximity: "lng,lat" string — biases results toward that location
function useGeoSearch(proximity?: string) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<GeoFeature[]>([]);
  const [picked, setPicked] = useState<GeoFeature | null>(null);
  const [searching, setSearching] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Keep latest proximity in a ref so the debounced callback always reads the current value
  const proximityRef = useRef(proximity);
  proximityRef.current = proximity;

  const onChange = (q: string) => {
    setQuery(q);
    setPicked(null);
    if (timer.current) clearTimeout(timer.current);
    if (!q.trim()) { setSuggestions([]); return; }
    timer.current = setTimeout(async () => {
      setSearching(true);
      const res = await geocode(q, proximityRef.current);
      setSuggestions(res.slice(0, 5));
      setSearching(false);
    }, 400);
  };

  const pick = (f: GeoFeature) => {
    setPicked(f);
    setQuery(f.place_name);
    setSuggestions([]);
  };

  const reset = () => { setQuery(''); setPicked(null); setSuggestions([]); };

  return { query, suggestions, picked, searching, onChange, pick, reset };
}

// ─── LocationsPanel ───────────────────────────────────────────────────────────
export function LocationsPanel() {
  const qc = useQueryClient();
  const { flyTo, activeProject, setActiveLocation, activeLocationId, viewport } = useMapStore();

  // Bias address suggestions toward the current map viewport
  const geo = useGeoSearch(`${viewport.lng},${viewport.lat}`);
  const [saveName, setSaveName] = useState('');
  const [pickerLocId, setPickerLocId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [gpsLoading, setGpsLoading] = useState(false);

  const handleGps = () => {
    if (!navigator.geolocation) { return; }
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { longitude, latitude } = pos.coords;
        try {
          const results = await geocode(`${longitude},${latitude}`);
          if (results.length > 0) {
            geo.pick(results[0]);
            setSaveName(results[0].place_name.split(',')[0]);
            flyTo(longitude, latitude);
          }
        } finally {
          setGpsLoading(false);
        }
      },
      () => setGpsLoading(false),
      { timeout: 10000 },
    );
  };

  const { data, isLoading } = useQuery({
    queryKey: ['locations'],
    queryFn: async () => {
      const result = await api.get<{ locations: Location[] }>('/locations');
      return result.success ? result.data.locations : [];
    },
  });

  const { data: scenes = [] } = useQuery({
    queryKey: ['scenes', activeProject?.id],
    queryFn: async () => {
      if (!activeProject) return [];
      const result = await api.get<{ scenes: Scene[] }>(`/scenes?projectId=${activeProject.id}&limit=200`);
      return result.success ? result.data.scenes : [];
    },
    enabled: !!activeProject && pickerLocId !== null,
  });

  const createMutation = useMutation({
    mutationFn: (body: { name: string; businessName?: string; address: string; lat: number; lng: number }) =>
      api.post<Location>('/locations', body),
    onSuccess: async (result) => {
      if (!result.success) { toast.error(result.error); return; }
      const loc = result.data;
      qc.invalidateQueries({ queryKey: ['locations'] });
      toast.success('Location saved');
      geo.reset();
      setSaveName('');

      // Auto-find nearest hospital and add as a support location
      try {
        const hits = await geocode('hospital', `${loc.lng},${loc.lat}`, 'poi', 1);
        if (hits.length > 0) {
          const h = hits[0];
          await api.post(`/locations/${loc.id}/supports`, {
            name: 'Nearest Hospital',
            address: h.place_name,
            lat: h.center[1],
            lng: h.center[0],
          });
          qc.invalidateQueries({ queryKey: ['location-supports', loc.id] });
        }
      } catch {
        // Non-critical — silently skip if hospital lookup fails
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/locations/${id}`),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ['locations'] });
      if (activeLocationId === id) setActiveLocation(null);
      toast.success('Location deleted');
    },
  });

  const addToSceneMutation = useMutation({
    mutationFn: ({ location, sceneId }: { location: Location; sceneId: string }) =>
      api.post('/pins', { sceneId, locationId: location.id, title: location.name, address: location.address, lat: location.lat, lng: location.lng, pinType: 'main' }),
    onSuccess: (result, { sceneId }) => {
      if (!result.success) { toast.error(result.error); return; }
      qc.invalidateQueries({ queryKey: ['pins', sceneId] });
      toast.success('Added to scene');
      setPickerLocId(null);
    },
  });

  const toggleExpand = (id: string) => {
    const next = expandedId === id ? null : id;
    setExpandedId(next);
    setActiveLocation(next);
    if (next) {
      const loc = (data ?? []).find(l => l.id === next);
      if (loc) flyTo(loc.lng, loc.lat);
    }
  };

  const locations = data ?? [];

  return (
    <div className="space-y-3">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400">
        Locations ({locations.length})
      </h3>

      {/* Add location search */}
      <div className="space-y-1.5">
        <div className="flex gap-1.5">
          <div className="relative flex-1">
            <input
              type="text"
              value={geo.query}
              onChange={e => geo.onChange(e.target.value)}
              placeholder="Search address to save…"
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-brand-400 pr-8"
            />
            {geo.searching && <div className="absolute right-2 top-2.5"><LoadingSpinner size="sm" /></div>}
            {geo.suggestions.length > 0 && (
              <ul className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                {geo.suggestions.map(f => (
                  <li key={f.id} className="px-3 py-2 text-sm text-gray-700 hover:bg-brand-50 cursor-pointer truncate" onClick={() => { geo.pick(f); setSaveName(f.place_name.split(',')[0]); }}>
                    {f.place_name}
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button
            onClick={handleGps}
            disabled={gpsLoading}
            title="Use my current location"
            className="px-2.5 py-2 bg-gray-100 hover:bg-brand-50 border border-gray-200 rounded-lg text-sm disabled:opacity-50 shrink-0"
          >
            {gpsLoading ? <LoadingSpinner size="sm" /> : '📍'}
          </button>
        </div>
        {geo.picked && (
          <div className="space-y-1.5">
            <input
              type="text"
              value={saveName}
              onChange={e => setSaveName(e.target.value)}
              placeholder="Your name for this location"
              className="w-full text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:border-brand-400"
            />
            <BusinessNameField onSave={(bizName) => {
              if (!geo.picked || !saveName.trim()) return;
              createMutation.mutate({ name: saveName.trim(), businessName: bizName, address: geo.picked.place_name, lat: geo.picked.center[1], lng: geo.picked.center[0] });
            }} saving={createMutation.isPending} canSave={!!saveName.trim()} />
          </div>
        )}
      </div>

      {/* Location list */}
      {isLoading ? (
        <div className="flex justify-center py-4"><LoadingSpinner size="sm" /></div>
      ) : locations.length === 0 ? (
        <p className="text-xs text-gray-400 text-center py-3">No saved locations yet</p>
      ) : (
        <div className="space-y-2">
          {locations.map(loc => (
            <LocationCard
              key={loc.id}
              location={loc}
              isExpanded={expandedId === loc.id}
              scenes={scenes}
              hasProject={!!activeProject}
              isPickerOpen={pickerLocId === loc.id}
              onToggleExpand={() => toggleExpand(loc.id)}
              onFly={() => flyTo(loc.lng, loc.lat)}
              onDelete={() => { if (confirm('Delete this location?')) deleteMutation.mutate(loc.id); }}
              onAddToScene={sceneId => addToSceneMutation.mutate({ location: loc, sceneId })}
              onTogglePicker={() => setPickerLocId(pickerLocId === loc.id ? null : loc.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Business name field used in the save form ───────────────────────────────
function BusinessNameField({ onSave, saving, canSave }: { onSave: (bizName: string) => void; saving: boolean; canSave: boolean }) {
  const [bizName, setBizName] = useState('');
  return (
    <div className="flex gap-1.5">
      <input
        type="text"
        value={bizName}
        onChange={e => setBizName(e.target.value)}
        placeholder="Business name (e.g. The Barrel)"
        className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-1.5 focus:outline-none focus:border-brand-400"
      />
      <button
        onClick={() => onSave(bizName.trim())}
        disabled={saving || !canSave}
        className="px-3 py-1.5 bg-brand-500 text-white text-sm rounded-lg hover:bg-brand-600 disabled:opacity-50 shrink-0"
      >
        Save
      </button>
    </div>
  );
}

// ─── LocationCard ─────────────────────────────────────────────────────────────
function LocationCard({
  location, isExpanded, scenes, hasProject,
  isPickerOpen, onToggleExpand, onFly, onDelete, onAddToScene, onTogglePicker,
}: {
  location: Location;
  isExpanded: boolean;
  scenes: Scene[];
  hasProject: boolean;
  isPickerOpen: boolean;
  onToggleExpand: () => void;
  onFly: () => void;
  onDelete: () => void;
  onAddToScene: (sceneId: string) => void;
  onTogglePicker: () => void;
}) {
  const qc = useQueryClient();
  const photoInputRef = useRef<HTMLInputElement>(null);
  const [addingPhoto, setAddingPhoto] = useState(false);
  const [scName, setScName] = useState(location.siteContactName);
  const [scPhone, setScPhone] = useState(location.siteContactPhone);
  const [scEmail, setScEmail] = useState(location.siteContactEmail);
  const [bizName, setBizName] = useState(location.businessName);
  const scTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const photos: string[] = (() => { try { return JSON.parse(location.photosJson); } catch { return []; } })();

  const updatePhotosMutation = useMutation({
    mutationFn: (photosJson: string) => api.put(`/locations/${location.id}`, { photosJson }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['locations'] }),
    onError: () => toast.error('Failed to update photos'),
  });

  const saveSiteContact = (patch: { siteContactName?: string; siteContactPhone?: string; siteContactEmail?: string; businessName?: string }) => {
    if (scTimer.current) clearTimeout(scTimer.current);
    scTimer.current = setTimeout(() => {
      api.put(`/locations/${location.id}`, patch).then(() => qc.invalidateQueries({ queryKey: ['locations'] }));
    }, 800);
  };

  const handlePhotoAdd = async (file: File) => {
    if (file.size > 5_000_000) { toast.error('File too large (max 5 MB)'); return; }
    setAddingPhoto(true);
    try {
      const dataUrl = await resizeImage(file);
      const updated = JSON.stringify([...photos, dataUrl]);
      updatePhotosMutation.mutate(updated);
    } catch {
      toast.error('Failed to process image');
    } finally {
      setAddingPhoto(false);
    }
  };

  const handlePhotoDelete = (idx: number) => {
    const updated = photos.filter((_, i) => i !== idx);
    updatePhotosMutation.mutate(JSON.stringify(updated));
  };

  return (
    <div className={`rounded-xl border transition-colors ${isExpanded ? 'border-brand-300' : 'border-gray-200'}`}>
      {/* Header row */}
      <div className="flex items-center gap-2 px-3 py-2.5">
        <span className="text-base shrink-0">📌</span>
        <div className="flex-1 min-w-0 cursor-pointer" onClick={onToggleExpand}>
          <p className="text-sm font-medium text-gray-800 truncate">{location.businessName || location.name}</p>
          {location.businessName && <p className="text-xs text-gray-500 truncate">{location.name}</p>}
          <p className="text-xs text-gray-400 truncate">{location.address}</p>
        </div>
        <div className="flex gap-1 shrink-0 items-center">
          <button onClick={onFly} title="Fly to" className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-brand-500 text-xs">🎯</button>
          <div className="relative">
            <button onClick={onTogglePicker} title={hasProject ? 'Add to scene' : 'Select a project first'} disabled={!hasProject} className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-brand-500 text-xs disabled:opacity-40">📎</button>
            {isPickerOpen && (
              <div className="absolute right-0 top-7 z-50 bg-white border border-gray-200 rounded-lg shadow-xl min-w-44 overflow-hidden">
                {scenes.length === 0 ? (
                  <p className="text-xs text-gray-400 px-3 py-2 text-center">No scenes in this project</p>
                ) : (
                  <ul className="max-h-48 overflow-y-auto divide-y divide-gray-50">
                    {scenes.map(s => (
                      <li key={s.id} className="px-3 py-2 text-sm text-gray-700 hover:bg-brand-50 cursor-pointer" onClick={() => onAddToScene(s.id)}>{s.name}</li>
                    ))}
                  </ul>
                )}
                <div className="border-t border-gray-100 px-3 py-1.5">
                  <button onClick={onTogglePicker} className="text-xs text-gray-400 hover:text-gray-600">Cancel</button>
                </div>
              </div>
            )}
          </div>
          <button onClick={onDelete} title="Delete" className="p-1 hover:bg-red-50 rounded text-gray-400 hover:text-red-500 text-xs">✕</button>
          <button onClick={onToggleExpand} className="p-1 text-gray-300 hover:text-gray-500 text-xs">{isExpanded ? '▲' : '▼'}</button>
        </div>
      </div>

      {/* Expanded content */}
      {isExpanded && (
        <div className="border-t border-gray-100 px-3 pb-3 space-y-3 bg-gray-50 rounded-b-xl">
          {/* Photos */}
          <div className="pt-2">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Photos</p>
            <div className="flex flex-wrap gap-1.5">
              {photos.map((src, i) => (
                <div key={i} className="relative group w-14 h-14 rounded-lg overflow-hidden border border-gray-200 shrink-0">
                  <img src={src} alt="" className="w-full h-full object-cover cursor-pointer" onClick={() => window.open(src, '_blank')} />
                  <button
                    onClick={() => handlePhotoDelete(i)}
                    className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full w-4 h-4 text-[10px] flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >✕</button>
                </div>
              ))}
              <button
                onClick={() => photoInputRef.current?.click()}
                disabled={addingPhoto}
                className="w-14 h-14 rounded-lg border border-dashed border-gray-300 hover:border-brand-400 flex items-center justify-center text-gray-400 hover:text-brand-500 text-xl transition-colors disabled:opacity-50 shrink-0"
                title="Add photo"
              >
                {addingPhoto ? <LoadingSpinner size="sm" /> : '+'}
              </button>
              <input ref={photoInputRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={e => { const files = Array.from(e.target.files ?? []); files.forEach(f => handlePhotoAdd(f)); e.target.value = ''; }} />
            </div>
          </div>

          {/* Business Name */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Business Name</p>
            <input
              type="text"
              value={bizName}
              onChange={e => {
                setBizName(e.target.value);
                saveSiteContact({ businessName: e.target.value } as Parameters<typeof saveSiteContact>[0]);
              }}
              placeholder="e.g. The Barrel, Pete's Fresh Market"
              className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-brand-400"
            />
          </div>

          {/* Site Contact */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Site Contact</p>
            <div className="space-y-1.5">
              <input
                type="text"
                value={scName}
                onChange={e => { setScName(e.target.value); saveSiteContact({ siteContactName: e.target.value }); }}
                placeholder="Contact name"
                className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-brand-400"
              />
              <div className="grid grid-cols-2 gap-1.5">
                <input
                  type="tel"
                  value={scPhone}
                  onChange={e => { setScPhone(e.target.value); saveSiteContact({ siteContactPhone: e.target.value }); }}
                  placeholder="Phone"
                  className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-brand-400"
                />
                <input
                  type="email"
                  value={scEmail}
                  onChange={e => { setScEmail(e.target.value); saveSiteContact({ siteContactEmail: e.target.value }); }}
                  placeholder="Email"
                  className="text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-brand-400"
                />
              </div>
            </div>
          </div>

          {/* Support locations */}
          <SupportLocations locationId={location.id} locationLng={location.lng} locationLat={location.lat} />
        </div>
      )}
    </div>
  );
}

// ─── SupportLocations ─────────────────────────────────────────────────────────
function SupportLocations({ locationId, locationLng, locationLat }: { locationId: string; locationLng: number; locationLat: number }) {
  const qc = useQueryClient();
  const { flyTo } = useMapStore();
  const [addOpen, setAddOpen] = useState(false);
  const [addName, setAddName] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  // Bias suggestions toward the parent location's coordinates
  const geo = useGeoSearch(`${locationLng},${locationLat}`);

  const { data, isLoading } = useQuery({
    queryKey: ['location-supports', locationId],
    queryFn: async () => {
      const result = await api.get<{ supports: LocationSupport[] }>(`/locations/${locationId}/supports`);
      return result.success ? result.data.supports : [];
    },
  });

  const createMutation = useMutation({
    mutationFn: (body: { name: string; address: string; lat: number; lng: number }) =>
      api.post(`/locations/${locationId}/supports`, body),
    onSuccess: (result) => {
      if (!result.success) { toast.error(result.error); return; }
      qc.invalidateQueries({ queryKey: ['location-supports', locationId] });
      toast.success('Support location added');
      setAddOpen(false);
      setAddName('');
      geo.reset();
    },
  });

  const renameMutation = useMutation({
    mutationFn: ({ supportId, name }: { supportId: string; name: string }) =>
      api.put(`/locations/${locationId}/supports?supportId=${supportId}`, { name }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['location-supports', locationId] });
      setEditId(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (supportId: string) => api.delete(`/locations/${locationId}/supports?supportId=${supportId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['location-supports', locationId] }),
  });

  const supports = data ?? [];

  return (
    <div>
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">Support Locations</p>

      {isLoading ? (
        <div className="flex justify-center py-2"><LoadingSpinner size="sm" /></div>
      ) : (
        <div className="space-y-1.5">
          {supports.map(sup => (
            <div key={sup.id} className="flex items-center gap-2 bg-white rounded-lg px-2.5 py-2 border border-amber-200">
              <div className="w-3 h-3 rounded-full bg-amber-400 shrink-0" />
              <div className="flex-1 min-w-0">
                {editId === sup.id ? (
                  <input
                    autoFocus
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    onBlur={() => { if (editName.trim() && editName !== sup.name) renameMutation.mutate({ supportId: sup.id, name: editName.trim() }); else setEditId(null); }}
                    onKeyDown={e => { if (e.key === 'Enter') { if (editName.trim()) renameMutation.mutate({ supportId: sup.id, name: editName.trim() }); else setEditId(null); } if (e.key === 'Escape') setEditId(null); }}
                    className="text-xs font-medium text-gray-700 w-full bg-transparent border-b border-amber-400 focus:outline-none"
                  />
                ) : (
                  <p className="text-xs font-medium text-gray-700 truncate cursor-pointer hover:text-amber-600" title="Click to rename" onClick={() => { setEditId(sup.id); setEditName(sup.name); }}>{sup.name}</p>
                )}
                <p className="text-xs text-gray-400 truncate">{sup.address}</p>
              </div>
              <button onClick={() => flyTo(sup.lng, sup.lat)} className="text-gray-400 hover:text-amber-500 text-xs p-0.5" title="Fly to">🎯</button>
              <button onClick={() => deleteMutation.mutate(sup.id)} className="text-gray-300 hover:text-red-500 text-xs p-0.5">✕</button>
            </div>
          ))}

          {/* Add support form */}
          {addOpen ? (
            <div className="bg-white rounded-lg border border-amber-200 p-2.5 space-y-1.5">
              <div className="relative">
                <input
                  type="text"
                  value={geo.query}
                  onChange={e => { geo.onChange(e.target.value); setAddName(e.target.value.split(',')[0]); }}
                  placeholder="Search address…"
                  className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-amber-400"
                />
                {geo.searching && <div className="absolute right-2 top-1.5"><LoadingSpinner size="sm" /></div>}
                {geo.suggestions.length > 0 && (
                  <ul className="absolute z-50 w-full mt-0.5 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                    {geo.suggestions.map(f => (
                      <li key={f.id} className="px-2.5 py-1.5 text-xs text-gray-700 hover:bg-amber-50 cursor-pointer truncate" onClick={() => { geo.pick(f); setAddName(f.place_name.split(',')[0]); }}>
                        {f.place_name}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {geo.picked && (
                <input
                  type="text"
                  value={addName}
                  onChange={e => setAddName(e.target.value)}
                  placeholder="Name / utility (e.g. Generator, Catering)"
                  className="w-full text-xs border border-gray-200 rounded-lg px-2.5 py-1.5 focus:outline-none focus:border-amber-400"
                />
              )}
              <div className="flex gap-1.5">
                <button
                  onClick={() => {
                    if (!geo.picked || !addName.trim()) return;
                    createMutation.mutate({ name: addName.trim(), address: geo.picked.place_name, lat: geo.picked.center[1], lng: geo.picked.center[0] });
                  }}
                  disabled={!geo.picked || !addName.trim() || createMutation.isPending}
                  className="flex-1 py-1 bg-amber-500 text-white text-xs rounded-lg hover:bg-amber-600 disabled:opacity-50"
                >Add</button>
                <button onClick={() => { setAddOpen(false); geo.reset(); setAddName(''); }} className="px-2 text-xs text-gray-400 hover:text-gray-600">Cancel</button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setAddOpen(true)}
              className="w-full text-xs text-amber-600 hover:text-amber-700 font-medium py-1.5 border border-dashed border-amber-300 hover:border-amber-400 rounded-lg transition-colors"
            >
              + Add support location
            </button>
          )}
        </div>
      )}
    </div>
  );
}
